import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { io } from 'socket.io-client';

import { QueryKey } from '../constants/index';
import { HttpException } from '../exceptions/index';
import { canSeeActivityCenter } from '../guards/page-access.util';
import { Company, NotificationHooks, useInvalidateQueries, User } from '../hooks/index';
import {
  SPACES_LIVE_DEBOUNCE_MS,
  SPACES_LIVE_QUERY_KEYS,
} from '../pages/data-room/constants/spaces-query.const';
import { useSafeConversionProgressStore } from '../pages/safes/convert-modal/conversion-progress.store';
import { SAFE_CONVERSION_RELATED_QUERY_KEYS } from '../pages/safes/hooks/use-safe-conversion-progress-fallback';
import { SOCKET_IO_CLIENT_BASE_OPTIONS } from '../sockets/socket-io-client-options';
import { ensureFreshAccessTokenForWs } from '../sockets/ws-ensure-access-token.util';
import { accessTokenStorage } from '../storage/auth/index';
import { AppNotification, NotificationType } from '../types/notification.types';
import { getScenarioSocketUrl } from '../utils/global.util';
import { toNumber } from '../utils/to-number.util';
import { toPositive } from '../utils/to-positive.util';

interface NotificationsContext {
  handleReadAllNotification: () => void;
  handleReadNotification: (type: NotificationType) => void;
  refreshUnreadCount: () => void;
  unreadNotification: { total: number } & Record<NotificationType, number>;
}

const initialValue: NotificationsContext = {
  unreadNotification: { activity: 0, total: 0, request: 0 },
  handleReadNotification() {},
  handleReadAllNotification() {},
  refreshUnreadCount() {},
};

export const notificationsContext = createContext(initialValue);

export interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: FC<NotificationsProviderProps> = ({ children }) => {
  const { selectedCompany, selectedCompanyId: companyId } = Company.useSelected();
  const { user } = User.useUser();
  const canSeeActivity = !selectedCompany || canSeeActivityCenter(selectedCompany);

  const {
    unread,
    dataUpdatedAt: totalUpdatedAt,
    isLoading,
  } = NotificationHooks.useUnreadNotificationCount({
    companyId,
    enabled: canSeeActivity,
    aminInAppNotifications: user?.adminInAppNotifications,
    stakeholderInAppNotifications: user?.stakeholderInAppNotifications,
  });

  const {
    unread: activityUnread,
    dataUpdatedAt: activityUpdatedAt,
    isLoading: isLoadingActivity,
  } = NotificationHooks.useUnreadNotificationCount({
    companyId,
    enabled: canSeeActivity,
    type: NotificationType.ACTIVITY,
    aminInAppNotifications: user?.adminInAppNotifications,
    stakeholderInAppNotifications: user?.stakeholderInAppNotifications,
  });

  const {
    unread: requestUnread,
    dataUpdatedAt: requestUpdatedAt,
    isLoading: isLoadingRequest,
  } = NotificationHooks.useUnreadNotificationCount({
    companyId,
    enabled: canSeeActivity,
    type: NotificationType.REQUEST,
    aminInAppNotifications: user?.adminInAppNotifications,
    stakeholderInAppNotifications: user?.stakeholderInAppNotifications,
  });

  const [unreadNotification, setUnread] = useState<
    { total: number } & Record<NotificationType, number>
  >({
    activity: 0,
    total: 0,
    request: 0,
  });

  // Keyed on `dataUpdatedAt`, not on the counts: the optimistic +1/-1 below mutates this mirror
  // independently of the query cache, so a refetch that returns the same number it returned last
  // time changes no count dependency and would leave the drift in place. Every successful read
  // must overwrite the mirror with server truth.
  useEffect(() => {
    if (isLoading || isLoadingActivity || isLoadingRequest) return;

    setUnread({
      request: toNumber(requestUnread.count),
      activity: toNumber(activityUnread.count),
      total: toNumber(unread.count),
    });
  }, [
    activityUnread.count,
    activityUpdatedAt,
    isLoading,
    isLoadingRequest,
    isLoadingActivity,
    requestUnread.count,
    requestUpdatedAt,
    unread.count,
    totalUpdatedAt,
  ]);

  const handleReadNotification = (type: NotificationType) =>
    setUnread((prev) => ({
      ...prev,
      [type]: toPositive(prev[type] - 1),
      total: toPositive(prev.total - 1),
    }));

  const handleReadAllNotification = () =>
    setUnread({
      activity: 0,
      total: 0,
      request: 0,
    });

  const queryClient = useQueryClient();

  // The badge is a locally-mutated counter (+1 per websocket push, -1 per mark-read) that only
  // re-syncs when the unread-count query refetches. Notifications can also vanish server-side with
  // no push and no mutation of ours — deleting an event cascades vesting task → exercise request →
  // request notification, and another admin can resolve a request — so anything that surfaces the
  // counts to the user must force a re-read first, or the badge keeps the pre-delete number while
  // the list already shows one fewer.
  const refreshUnreadCount = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_UNREAD_NOTIFICATION_COUNT] });
  }, [queryClient]);

  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_NOTIFICATIONS);
  const invalidateNotificationsDebouncedRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const invalidateSpacesDebouncedRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Reconnected whenever the selected company changes, `companyId` below.
   *
   * The server decides who hears a company's events from the stakeholder it resolves while the
   * socket is connecting, and it never revisits it. A tab that connects before a company is
   * selected — a fresh sign-in, or a guest who accepts a data room invitation and is moved into
   * the company a moment later — is therefore recorded as belonging to no company at all, and is
   * told about nothing for as long as it stays open. Switching companies leaves the same stale
   * answer behind. Reconnecting is what makes the server look again.
   */
  useEffect(() => {
    const buildAuthPayload = () => ({
      isAdmin: false as const,
      accessToken: accessTokenStorage.getState().get() || '',
    });

    const socket = io(getScenarioSocketUrl('notifications'), {
      ...SOCKET_IO_CLIENT_BASE_OPTIONS,
      auth: (cb) => {
        void ensureFreshAccessTokenForWs().finally(() => {
          cb(buildAuthPayload());
        });
      },
    });

    socket.on('connect_error', (error) => console.error('Connection error:', error));
    socket.on('connect', () => console.log('Notifications ws connected'));
    socket.on('disconnect', (reason) => console.log('Notifications ws disconnect', reason));
    socket.on('error', (e: HttpException) => console.error(e));
    socket.on('notification', (notificationString: string) => {
      const { type } = JSON.parse<Pick<AppNotification, 'id' | 'type'>>(notificationString);
      setUnread((prev) => ({
        ...prev,
        total: prev.total + 1,
        [type]: toNumber(prev[type] + 1),
      }));

      clearTimeout(invalidateNotificationsDebouncedRef.current);
      invalidateNotificationsDebouncedRef.current = setTimeout(() => {
        invalidateNotificationsDebouncedRef.current = undefined;
        invalidateQuery();
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_UNREAD_NOTIFICATION_COUNT] });
      }, 500);
    });
    // What changed is this person's standing in a company, so both answers that depend on it have
    // to be re-read. The list as much as the selection: somebody put back into a company is
    // sitting on My Companies when they are told, and that page reads the list — left alone it
    // keeps serving the cached one, which is the company still missing until a reload.
    socket.on('access-role-changed', () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SELECTED_COMPANY] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.GET_COMPANIES] });
    });
    // Somebody else changed a data room or a workspace of this company. Nothing here knows what,
    // and nothing needs to: these reads are all scoped on the server, so each one comes back as
    // whatever this person may now see. Without it a tab that stays visible never re-reads at all
    // — React Query refreshes on `visibilitychange`, which a second window never fires.
    socket.on('spaces-changed', () => {
      clearTimeout(invalidateSpacesDebouncedRef.current);
      invalidateSpacesDebouncedRef.current = setTimeout(() => {
        invalidateSpacesDebouncedRef.current = undefined;

        for (const queryKey of SPACES_LIVE_QUERY_KEYS) {
          void queryClient.invalidateQueries({ queryKey: [queryKey] });
        }
      }, SPACES_LIVE_DEBOUNCE_MS);
    });
    socket.on('safe-conversion-completed', () => {
      useSafeConversionProgressStore.getState().clearConverting();
      for (const queryKey of SAFE_CONVERSION_RELATED_QUERY_KEYS) {
        void queryClient.invalidateQueries({ queryKey: [queryKey] });
      }
    });

    return () => {
      clearTimeout(invalidateNotificationsDebouncedRef.current);
      clearTimeout(invalidateSpacesDebouncedRef.current);
      socket.disconnect();
    };
  }, [companyId, invalidateQuery, queryClient]);

  return (
    <notificationsContext.Provider
      value={{
        unreadNotification,
        handleReadNotification,
        handleReadAllNotification,
        refreshUnreadCount,
      }}
    >
      {children}
    </notificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(notificationsContext);
